// Plain (unencrypted) JSON convention, same as suspensionApi.js -- the
// dropshippingInterne backend routes (server_elakay/dropshippingInterne)
// return plain res.json(), no encrypt/decrypt round-trip.
import { elakay_API } from "../../../config";
import { ROUTE_PREFIX } from "../constants/dropshippingInterneConstants";
import { prepareUpload } from "../../../utils/uploadPolicy";
import { readUploadResponse, withUploadRetry } from "../../../utils/uploadResponse";

const headers = (token) => ({
  "Content-Type": "application/json",
  ...(token && { Authorization: `Bearer ${token}` }),
});

const handle = async (response) => {
  const data = await response.json();
  if (!response.ok) {
    const error = new Error(data.error || "Network or server error.");
    error.status = response.status;
    error.code = data.code;
    // Some endpoints (e.g. linkProviderProductToShop's ALREADY_LINKED_RESTORABLE)
    // attach extra structured fields (sellerProductId, deleted, canBeRestored)
    // alongside error/code -- kept here so a specific caller can surface them
    // without changing this shared helper's return contract for every caller.
    error.details = data;
    throw error;
  }
  return data;
};

const BASE = `${elakay_API}${ROUTE_PREFIX}`;

// ── Provider: son propre catalogue ────────────────────────────────────────

export const createProviderProduct = async (token, payload) => {
  try {
    const response = await fetch(`${BASE}/provider/products`, {
      method: "POST",
      headers: headers(token),
      body: JSON.stringify(payload),
    });
    return await handle(response);
  } catch (error) {
    return { success: false, error: error.message, ...(error.code && { code: error.code }), ...(error.status && { status: error.status }) };
  }
};

export const listOwnProviderProducts = async (token, { status, page = 1, limit = 20, filters = {} } = {}) => {
  try {
    const query = new URLSearchParams();
    if (status) query.set("status", status);
    Object.entries(filters).forEach(([k, v]) => v !== "" && v !== false && v != null && query.set(k, v === true ? "1" : v));
    query.set("page", page);
    query.set("limit", limit);
    const response = await fetch(`${BASE}/provider/products?${query.toString()}`, {
      method: "GET",
      headers: headers(token),
    });
    return await handle(response);
  } catch (error) {
    return { success: false, error: error.message, ...(error.code && { code: error.code }), ...(error.status && { status: error.status }) };
  }
};

export const getOwnProviderProduct = async (token, id) => {
  try {
    const response = await fetch(`${BASE}/provider/products/${id}`, {
      method: "GET",
      headers: headers(token),
    });
    return await handle(response);
  } catch (error) {
    return { success: false, error: error.message, ...(error.code && { code: error.code }), ...(error.status && { status: error.status }) };
  }
};

// Sales Simulation Provider : seuls les paramètres de scénario partent au
// serveur (quantity, hypotheticalSellerPrice, sellerCount, ordersPerSeller) ;
// prix, paliers, MOQ, stock, options et taux de frais sont relus côté backend.
export const simulateProviderSales = async (token, id, params = {}) => {
  try {
    const response = await fetch(`${BASE}/provider/products/${id}/sales-simulation`, {
      method: "POST",
      headers: headers(token),
      body: JSON.stringify(params),
    });
    return await handle(response);
  } catch (error) {
    return { success: false, error: error.message, ...(error.code && { code: error.code }), ...(error.status && { status: error.status }) };
  }
};

// Sales Simulation en mode CREATE (/provider/products/add) : le produit n'existe
// pas forcément en base, les données du formulaire sont envoyées pour
// validation + calcul. Lecture seule côté serveur. `code`/`fields` permettent
// à l'UI de distinguer données manquantes, accès refusé et vraie panne.
export const previewProviderSales = async (token, product, params = {}) => {
  try {
    const response = await fetch(`${BASE}/provider/products/sales-simulation/preview`, {
      method: "POST",
      headers: headers(token),
      body: JSON.stringify({ ...params, product }),
    });
    const data = await response.json();
    if (!response.ok) {
      return { success: false, error: data.error || "Network or server error.", status: response.status, ...(data.code && { code: data.code }), ...(data.fields && { fields: data.fields }) };
    }
    return data;
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Edit Product : supprimer / choisir l'image principale (première) / réordonner
// des images DÉJÀ sur le produit. Ajouter passe uniquement par l'upload.
export const setProviderProductImages = async (token, id, images) => {
  try {
    const response = await fetch(`${BASE}/provider/products/${id}/images`, {
      method: "PUT",
      headers: headers(token),
      body: JSON.stringify({ images }),
    });
    return await handle(response);
  } catch (error) {
    return { success: false, error: error.message, ...(error.code && { code: error.code }), ...(error.status && { status: error.status }) };
  }
};

export const updateProviderProduct = async (token, id, patch) => {
  try {
    const response = await fetch(`${BASE}/provider/products/${id}`, {
      method: "PATCH",
      headers: headers(token),
      body: JSON.stringify(patch),
    });
    return await handle(response);
  } catch (error) {
    return { success: false, error: error.message, ...(error.code && { code: error.code }), ...(error.status && { status: error.status }) };
  }
};

// Étape "Images" du wizard -- une image par requête (multipart, champ "photo"),
// comme /products/updateGaleries côté Seller. Pas de Content-Type manuel : le
// navigateur pose lui-même la frontière multipart.
export const uploadProviderProductImage = async (token, id, file) => {
  try {
    const body = new FormData();
    body.append("photo", await prepareUpload("productImage", file));
    const response = await withUploadRetry(async () => {
      const answer = await fetch(`${BASE}/provider/products/${id}/images`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body,
      });
      // A busy server is retried; a proxy HTML page (413) gets a readable message.
      if (answer.status === 503 || !String(answer.headers?.get?.("content-type") || "application/json").includes("json")) await readUploadResponse(answer);
      return answer;
    });
    return await handle(response);
  } catch (error) {
    return { success: false, error: error.message, ...(error.code && { code: error.code }), ...(error.status && { status: error.status }) };
  }
};

export const softDeleteProviderProduct = async (token, id) => {
  try {
    const response = await fetch(`${BASE}/provider/products/${id}`, {
      method: "DELETE",
      headers: headers(token),
    });
    return await handle(response);
  } catch (error) {
    return { success: false, error: error.message, ...(error.code && { code: error.code }), ...(error.status && { status: error.status }) };
  }
};

// ── Seller (+ Provider/Administrator/Family) : catalogue privé ───────────

export const listProviderCatalogForSeller = async (token, { categoryId, providerId, shopId, page = 1, limit = 20, q, sort, filters } = {}) => {
  try {
    const query = appendProviderFilters(new URLSearchParams(), { ...filters, ...(categoryId && { categoryId }), ...(providerId && { providerId }), ...(providerId && shopId && { shopId }) });
    if (q) query.set("q", q);
    if (sort) query.set("sort", sort);
    query.set("page", page);
    query.set("limit", limit);
    const response = await fetch(`${BASE}/seller/products?${query.toString()}`, {
      method: "GET",
      headers: headers(token),
    });
    return await handle(response);
  } catch (error) {
    return { success: false, error: error.message, ...(error.code && { code: error.code }), ...(error.status && { status: error.status }) };
  }
};

// spec §28/§29 -- "Add Product Dropshipping In" recharge le produit Provider
// depuis l'URL (id) seule -- jamais uniquement depuis location.state, qui ne
// survivrait pas à un refresh navigateur.
export const getCatalogProductForSeller = async (token, id) => {
  try {
    const response = await fetch(`${BASE}/seller/products/${id}`, {
      method: "GET",
      headers: headers(token),
    });
    return await handle(response);
  } catch (error) {
    return { success: false, error: error.message, ...(error.code && { code: error.code }), ...(error.status && { status: error.status }) };
  }
};

// Global comments across every Seller Product linked to this Provider Product (ticket §4),
// paginated -- never all loaded at once.
export const getProviderProductComments = async (token, id, { skip = 0, limit = 20 } = {}) => {
  try {
    const query = new URLSearchParams({ skip: String(skip), limit: String(limit) });
    const response = await fetch(`${BASE}/seller/products/${id}/comments?${query.toString()}`, {
      method: "GET",
      headers: headers(token),
    });
    return await handle(response);
  } catch (error) {
    return { success: false, error: error.message, ...(error.code && { code: error.code }), ...(error.status && { status: error.status }) };
  }
};

// "Buy Wholesale" : devis validé côté serveur (MOQ, palier, stock, région). Ne crée
// aucune commande. `code` distingue BELOW_MOQ / INSUFFICIENT_STOCK / REGION_*.
export const getWholesaleQuote = async (token, id, quantity) => {
  try {
    const response = await fetch(`${BASE}/seller/products/${id}/wholesale-quote`, {
      method: "POST",
      headers: headers(token),
      body: JSON.stringify({ quantity }),
    });
    const data = await response.json();
    if (!response.ok) return { success: false, error: data.error || "Network or server error.", status: response.status, ...(data.code && { code: data.code }), ...data };
    return data;
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Sell Center "Provider / Producer In" / "Dropshipping In" -- matrice
// groupée par provider, aperçu capé côté serveur par AnimationSettings.
// sellCenter.providerProducerIn.productsPerProvider.
// Product-level criteria shared by the providers list and the facets endpoint. Kept in sync with
// sellerProviderCatalogService.js#applyProductFilters, which already reads every one of these --
// `country` (Provider's own address) and `pa` were readable server-side but missing here, so the
// Location and "PA discount available" filters in ProviderFiltersPanel silently did nothing; fixed
// alongside adding `madeIn` (ticket §1 -- the PRODUCT's country of manufacture, a different field).
// shopId (multi-shop): one Provider shop's products -- only meaningful with a single providerId.
const FILTER_KEYS = ["categoryId", "providerId", "shopId", "wholesale", "dropshipping", "pa", "inStock", "minPrice", "maxPrice", "maxMinimumQuantity", "country", "madeIn"];
export const appendProviderFilters = (query, filters = {}) => {
  FILTER_KEYS.forEach((k) => {
    const v = filters[k];
    if (v !== undefined && v !== null && v !== "" && v !== false) query.set(k, v === true ? "1" : v);
  });
  (filters.attr || []).forEach((a) => query.append("attr", a));
  return query;
};

export const listProviderFacets = async (token, filters = {}) => {
  try {
    const response = await fetch(`${BASE}/seller/providers/facets?${appendProviderFilters(new URLSearchParams(), filters)}`, { method: "GET", headers: headers(token) });
    return await handle(response);
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const listProviderShopsForSeller = async (token, { page = 1, limit = 12, search, sortBy, scope, filters } = {}) => {
  try {
    const query = appendProviderFilters(new URLSearchParams({ page, limit }), filters);
    if (search) query.set("search", search);
    if (sortBy) query.set("sortBy", sortBy);
    if (scope) query.set("scope", scope);
    const response = await fetch(`${BASE}/seller/providers?${query}`, {
      method: "GET",
      headers: headers(token),
    });
    return await handle(response);
  } catch (error) {
    return { success: false, error: error.message, ...(error.code && { code: error.code }), ...(error.status && { status: error.status }) };
  }
};

// spec §6/§7/§8 -- lier un ProviderProduct à sa boutique ("Add Product").
// compareAtPrice/visibility optionnels (spec §14/§15/§19, Add Product
// Dropshipping In) -- le backend les ignore/valide, jamais fait confiance
// aveuglément (spec §31).
export const linkProviderProductToShop = async (
  token,
  options
) => {
  try {
    const {
      providerProductId, sellerPrice, compareAtPrice, visibility, fulfillmentMode,
      sellerTitle, sellerDescription, filterSubset, acceptanceMode, pricingMode, desiredProfit,
    } = options || {};
    const response = await fetch(`${BASE}/seller/link-product`, {
      method: "POST",
      headers: headers(token),
      body: JSON.stringify({
        providerProductId, sellerPrice, compareAtPrice, visibility, fulfillmentMode, sellerTitle, sellerDescription, filterSubset,
        ...(acceptanceMode === "provider" ? { acceptanceMode } : {}),
        ...(pricingMode !== undefined ? { pricingMode } : {}), ...(desiredProfit !== undefined ? { desiredProfit } : {}),
      }),
    });
    return await handle(response);
  } catch (error) {
    return {
      success: false, error: error.message,
      ...(error.code && { code: error.code }), ...(error.status && { status: error.status }),
      // ALREADY_LINKED_RESTORABLE carries sellerProductId/deleted/canBeRestored
      // so the UI can offer "Restore to My Shop" instead of a dead-end error.
      ...(error.details?.sellerProductId && { sellerProductId: error.details.sellerProductId }),
      ...(error.details?.linkId && { linkId: error.details.linkId }),
      ...(typeof error.details?.deleted === "boolean" && { deleted: error.details.deleted }),
      ...(typeof error.details?.canBeRestored === "boolean" && { canBeRestored: error.details.canBeRestored }),
    };
  }
};

export const changeSellerPaymentAcceptanceMode = async (token, linkId, acceptanceMode) => {
  try {
    const response = await fetch(`${BASE}/seller/links/${linkId}/payment-acceptance`, {
      method: "PATCH", headers: headers(token), body: JSON.stringify({ acceptanceMode }),
    });
    return await handle(response);
  } catch (error) {
    return { success: false, error: error.message, ...(error.code && { code: error.code }), ...(error.status && { status: error.status }) };
  }
};

export const listSellerProviderLinks = async (token, { status = "active" } = {}) => {
  try {
    const query = new URLSearchParams();
    if (status) query.set("status", status);
    const response = await fetch(`${BASE}/seller/links?${query.toString()}`, {
      method: "GET",
      headers: headers(token),
    });
    return await handle(response);
  } catch (error) {
    return { success: false, error: error.message, ...(error.code && { code: error.code }), ...(error.status && { status: error.status }) };
  }
};

export const unlinkSellerProviderProduct = async (token, linkId) => {
  try {
    const response = await fetch(`${BASE}/seller/links/${linkId}`, {
      method: "DELETE",
      headers: headers(token),
    });
    return await handle(response);
  } catch (error) {
    return { success: false, error: error.message, ...(error.code && { code: error.code }), ...(error.status && { status: error.status }) };
  }
};

// Resynchronize an already-linked product from the CURRENT Provider Product
// (images/cost reference/filter subset only -- see the backend service for
// the exact scope). Returns { link, product, providerProduct }.
export const resyncSellerProviderLink = async (token, linkId) => {
  try {
    const response = await fetch(`${BASE}/seller/links/${linkId}/sync`, {
      method: "POST",
      headers: headers(token),
    });
    return await handle(response);
  } catch (error) {
    return { success: false, error: error.message, ...(error.code && { code: error.code }), ...(error.status && { status: error.status }) };
  }
};

// Explicit restore of an Unlinked or soft-deleted (trashed) linked product,
// on its SAME sellerProductId -- never automatic. Returns { link, product,
// providerProduct } just like resync (restoring also resynchronizes).
export const restoreSellerProviderLink = async (token, linkId) => {
  try {
    const response = await fetch(`${BASE}/seller/links/${linkId}/restore`, {
      method: "POST",
      headers: headers(token),
    });
    return await handle(response);
  } catch (error) {
    return { success: false, error: error.message, ...(error.code && { code: error.code }), ...(error.status && { status: error.status }) };
  }
};

// Provider Home: every section is computed server-side from real data (empty ones are empty arrays).
export const getProviderHome = async (token) => {
  try {
    const response = await fetch(`${BASE}/seller/providers/home`, { method: "GET", headers: headers(token) });
    return await handle(response);
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ── Provider Views / Provider Product Views / Provider Likes (ticket §9-§14) ───────────────
// Call sites are responsible for only calling record* once per REAL navigation (a useEffect keyed
// to the id, never on every re-render) -- see ProviderProductDetail.jsx / ProviderCatalog.jsx's
// own comments on this exact requirement.
export const recordProviderView = async (token, providerId) => {
  try {
    const response = await fetch(`${BASE}/seller/providers/${providerId}/view`, { method: "POST", headers: headers(token) });
    return await handle(response);
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const recordProviderProductView = async (token, providerProductId) => {
  try {
    const response = await fetch(`${BASE}/seller/products/${providerProductId}/view`, { method: "POST", headers: headers(token) });
    return await handle(response);
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Seller Sourcing History (Seller features only, server-enforced): max 15 Providers / 30 Provider
// Products, newest first. `limit` only narrows it (Swipers), never widens it.
export const getRecentlyViewedProviders = async (token, { limit } = {}) => {
  try {
    const response = await fetch(`${BASE}/seller/providers/recently-viewed${limit ? `?limit=${encodeURIComponent(limit)}` : ""}`, { method: "GET", headers: headers(token) });
    return await handle(response);
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getRecentlyViewedProviderProducts = async (token, { limit } = {}) => {
  try {
    const response = await fetch(`${BASE}/seller/products/recently-viewed${limit ? `?limit=${encodeURIComponent(limit)}` : ""}`, { method: "GET", headers: headers(token) });
    return await handle(response);
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const likeProvider = async (token, providerId) => {
  try {
    const response = await fetch(`${BASE}/seller/providers/${providerId}/like`, { method: "POST", headers: headers(token) });
    return await handle(response);
  } catch (error) {
    return { success: false, error: error.message, ...(error.code && { code: error.code }) };
  }
};

export const unlikeProvider = async (token, providerId) => {
  try {
    const response = await fetch(`${BASE}/seller/providers/${providerId}/like`, { method: "DELETE", headers: headers(token) });
    return await handle(response);
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getLikedProviders = async (token, { page = 1, limit = 20 } = {}) => {
  try {
    const query = new URLSearchParams({ page: String(page), limit: String(limit) });
    const response = await fetch(`${BASE}/seller/providers/liked?${query.toString()}`, { method: "GET", headers: headers(token) });
    return await handle(response);
  } catch (error) {
    return { success: false, error: error.message };
  }
};
